// TaskPage.tsx
import {
  faArrowLeft,
  faEllipsisV,
  faEye,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import DOMPurify from "dompurify";
import moment from "moment";
import React, { useEffect, useState, useRef } from "react";
import {
  Badge,
  Button,
  Dropdown,
  Modal,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import Offcanvas from "react-bootstrap/Offcanvas";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import avtar from "../../../assets/images/avtar.png";
import copylink from "../../../assets/images/copylink.svg";
import { ROLE_PRIORITY, ROLES } from "../../../Data/SD";
import { useAuth } from "../../../firebase/AuthProvider";
import Task from "../../../interfaces/task/Task";
import TaskAction from "../../../interfaces/task/taskActions/TaskAction";
import TaskWorkflow from "../../../interfaces/task/workflow/TaskWorkflow";
import { createConversation } from "../../../services/chatService/ChatService";
import { getTaskActionsByTaskId } from "../../../services/taskServices/TaskActionService";
import {
  getTaskById,
  lookupTask,
} from "../../../services/taskServices/TaskService";
import {
  approveTask,
  getTaskWorkflow,
} from "../../../services/taskServices/TaskWorkflowServices";
import {
  getCurrentWeeklySupervisionByEmployee,
  getTaskProgressNote,
  postSupervisionComment,
  TaskProgressNoteDto,
} from "../../../services/weeklySupervision/weeklySupervisionService";
import { getTimeAgo } from "../../../utilities/dateUtils";
import ClickToCopyDiv from "../../ui/ClickToCopyContainer/ClickToCopyDiv";
import Unathorized from "../errorPages/Unauthorized";
import CreateTaskActivityComponent from "../taskActivity/CreateTaskActivityComponent";
import AddTaskMeetingModal from "./Modals/AddTaskMeetingModal";
import LateTaskJustificationModal from "./Modals/LateTaskJustificationModal";
import RequestTaskClosureModal from "./Modals/RequestTaskClosureModal";
import RequestTaskExtensionModal from "./Modals/RequestTaskExtensionModal";
import UpdateTaskActivityModal from "./Modals/UpdateTaskActivityModal";
import RejectTaskModal from "./RejectTaskModal";
import TaskAttachmentSection from "./TaskAttachmentSection";
import TaskChatComponent from "./TaskChatComponent/TaskChatComponent";
import "./taskpage.css";
import { User } from "../../../interfaces/User";
import { ArrowLeft, Clock, Download, Trash } from "lucide-react";
import TaskMeetingSection from "./TaskMeetingSection";
import ModalMeet from "../calendar/ModalMeet";
import AddEvent from "../calendar/events/AddEvent";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import "../calendar/calender.css";
import NoDataFound from "./NoDataFound";
import TaskActivitySection from "./TaskActivitySection";
import TaskSidebar from "./TaskSidebar";
import InstructionsPopup from "./InstructionsPopup";

const TaskPage: React.FC = () => {
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [justificationType, setJustificationType] = useState<
    "LATE" | "AT-RISK"
  >("LATE");
  const { currentUser, currentRoles, currentOrganization } = useAuth();
  const [workflowItems, setWorkflowItems] = useState<TaskWorkflow[]>();
  const navigate = useNavigate();
  const { taskId } = useParams();
  const [task, setTask] = useState<Task | null>(null);
  console.log("🚀 ~ TaskPage ~ task:", task)
  const [isLoadingTask, setIsLoadingTask] = useState<boolean>(true);
  const [unauthorized, setUnauthorized] = useState<boolean>(false);
  const [authToken, setAuthToken] = useState<string>("");
  const [showAddMeetingModal, setShowAddMeetingModal] =
    useState<boolean>(false);
  const [showCloseTaskRequestModal, setShowCloseTaskRequestModal] =
    useState(false);
  const [showExtendTaskRequestModal, setShowExtendTaskRequestModal] =
    useState(false);
  const [showRejectTaskModal, setShowRejectTaskModal] = useState(false);
  const [showUpdateActivityModal, setShowUpdateActivityModal] = useState(false);
  const [currentActivity, setCurrentActivity] = useState<TaskAction>();
  const [showLateTaskJustificationModal, setShowLateTaskJustificationModal] =
    useState(false);
  const [lodingFor, setLoadingFor] = useState<string | null>("");
  const [activities, setActivities] = useState<
    TaskAction[] | null | undefined
  >();

  // Note: Sidebar logic moved, but main tabs logic stays
  const [activeMainTab, setActiveMainTab] = useState<
    "activity" | "comments" | "attachments" | "meetings"
  >("activity");
  const [attachmentCount, setAttachmentCount] = useState<number>(0);
  const [progressNotes, setProgressNotes] = useState("");
  const [progressNoteData, setProgressNoteData] =
    useState<TaskProgressNoteDto | null>(null);
  const [isNoteLoading, setIsNoteLoading] = useState(false);
  const [supervisionInstanceId, setSupervisionInstanceId] = useState<
    string | undefined
  >(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showInstructionsPopup, setShowInstructionsPopup] = useState(false);
  const instructionsIconRef = React.useRef<SVGSVGElement>(null);

  // Helper to extract the Friendly ID integer from "ES-100"
  const getFriendlyIdFromUrl = (urlParam: string | undefined): string => {
    if (!urlParam) return "";
    if (urlParam.includes("-")) {
      return urlParam.split("-")[1];
    }
    return urlParam;
  };

  // Format timestamp with UTC handling
  const formatActivityTime = (date: Date | string | undefined) => {
    if (!date) return "N/A";
    const now = new Date();
    let messageDate: Date;
    if (typeof date === "string") {
      if (
        date.includes("T") &&
        !date.includes("Z") &&
        !date.includes("GMT") &&
        !date.match(/[+-]\d{2}:\d{2}$/)
      ) {
        messageDate = new Date(date + "Z");
      } else {
        messageDate = new Date(date);
      }
    } else {
      messageDate = date;
    }

    if (isNaN(messageDate.getTime())) {
      return "N/A";
    }

    const timeStr = messageDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDay = new Date(
      messageDate.getFullYear(),
      messageDate.getMonth(),
      messageDate.getDate()
    );
    const diffInMs = today.getTime() - messageDay.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return timeStr;
    else if (diffInDays === 1) return `Yesterday ${timeStr}`;
    else if (diffInDays < 7) {
      const dayName = messageDate.toLocaleDateString("en-US", {
        weekday: "short",
      });
      return `${dayName} ${timeStr}`;
    } else if (messageDate.getFullYear() === now.getFullYear()) {
      const dateStr = messageDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      return `${dateStr}, ${timeStr}`;
    } else {
      const dateStr = messageDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return `${dateStr}, ${timeStr}`;
    }
  };

  useEffect(() => {
    if (!currentUser || !task) return;

    const fetchSupervisionInstance = async () => {
      const assignee = task.assignees?.find(
        (a) => !a.isCoAssignee && !a.isReporter
      );
      if (assignee?.user.id) {
        try {
          const token = await currentUser.getIdToken();
          const response = await getCurrentWeeklySupervisionByEmployee(
            token,
            assignee.user.id,
            task.subType?.type || "",
            1,
            1
          );
          if (response?.supervisionInstance?.id) {
            setSupervisionInstanceId(response.supervisionInstance.id);
          }
        } catch (error) {
          console.error("Error fetching current supervision instance:", error);
        }
      }
    };
    fetchSupervisionInstance();
  }, [currentUser, task]);

  useEffect(() => {
    if (!currentUser || !taskId) return;
    const friendlyId = getFriendlyIdFromUrl(taskId);

    const loadTask = async () => {
      const token = await currentUser.getIdToken();
      setAuthToken(token);
      if (friendlyId) {
        await fetchTask(friendlyId, token);
      }
    };
    loadTask();
  }, [taskId, currentUser]);

  useEffect(() => {
    if (!currentUser || !task || !authToken) return;
    const loadTaskData = async () => {
      await fetchTaskActivities(String(task.id), authToken);
      await fetchWorkflows(String(task.id));
      await fetchNote(String(task.id));
    };

    loadTaskData();
  }, [task?.id, currentUser, authToken]);

  const reload = () => {
    const friendlyId = getFriendlyIdFromUrl(taskId);
    if (friendlyId && authToken) {
      fetchTask(friendlyId, authToken);
      if (task?.id) {
        const systemId = String(task.id);
        fetchTaskActivities(systemId, authToken);
        fetchWorkflows(systemId);
        fetchNote(systemId);
      }
    }
  };

  const handleMeetingCreated = () => {
    reload();
    setActiveMainTab("meetings");
  };

  const handleActivityCreated = async () => {
    setShowAddActivity(false);
    // Immediately refetch activities after creation
    if (task?.id && currentUser) {
      try {
        const token = await currentUser.getIdToken();
        await fetchTaskActivities(String(task.id), token);
        // Also refetch task data to update counts
        const friendlyId = getFriendlyIdFromUrl(taskId);
        if (friendlyId) {
          await fetchTask(friendlyId, token);
        }
        await fetchWorkflows(String(task.id));
      } catch (error) {
        console.error("Error refreshing activities after creation:", error);
      }
    } else {
      // Fallback to reload if task or user is not available
      reload();
    }
  };

  const fetchTask = async (friendly_Id: string, token: string) => {
    try {
      setIsLoadingTask(true);
      const fetchedTask = await lookupTask(friendly_Id, token);
      setTask(fetchedTask);
    } catch (error: any) {
      if (error.message === "Unauthorized") setUnauthorized(true);
      else console.error("Error fetching task", error);
    } finally {
      setIsLoadingTask(false);
    }
  };

  const fetchWorkflows = async (system_Id: string) => {
    if (!currentUser) return;
    const token = await currentUser.getIdToken();
    const fetchedWorkflows = await getTaskWorkflow(system_Id, token);
    const sortedResponse = fetchedWorkflows?.sort(
      (a, b) => a.stepOrder - b.stepOrder
    );
    setWorkflowItems(sortedResponse);
  };

  const fetchTaskActivities = async (system_Id: string, token: string) => {
    const fetchedActivities = await getTaskActionsByTaskId(
      system_Id,
      token
    );
    setActivities(fetchedActivities);
  };

  const fetchNote = async (system_Id: string) => {
    try {
      setIsNoteLoading(true);
      const token = await currentUser!.getIdToken();
      const noteData = await getTaskProgressNote(token, system_Id);

      if (noteData) {
        setProgressNoteData(noteData);
        setProgressNotes("");
      } else {
        setProgressNoteData(null);
        setProgressNotes("");
      }
    } catch (error) {
      console.error("Failed to load progress note", error);
    } finally {
      setIsNoteLoading(false);
    }
  };

  const closureRequest = task?.administrativeTransactionRequests?.find(
    (req) =>
      req.transactionType === "Task Deletion Request" &&
      req.status === "PENDING"
  );

  // Re-use logic for button styling in header only, sidebar handled internally
  const getDueDateStatusForLogic = () => {
    if (!task || ["COMPLETED", "DELETED"].includes(task.status.name)) {
      return { isLate: false };
    }
    const daysUntilDue = moment(task.endDate).startOf("day").diff(moment().startOf("day"), "days");
    if (daysUntilDue < 0) return { isLate: true };
    return { isLate: false };
  };

  const HandleApprove = async () => {
    const systemTaskId = String(task?.id);
    if (!currentUser || !systemTaskId) return;

    // Use logic just for modal decision
    const currentDueDateStatus = getDueDateStatusForLogic();

    if (currentDueDateStatus.isLate) {
      // Check if justification has already been provided
      if (task?.justificationReason && task?.justificationType) {
        // Justification already submitted, proceed with approval
        setLoadingFor("ApproveReject");
        try {
          const token = await currentUser.getIdToken();
          const res = await approveTask(systemTaskId, token);

          if (res.success) {
            toast.success("Task successfully approved!");
            reload();
          } else {
            toast.error(res.message);
          }
        } catch (err: any) {
          toast.error(err.message);
        } finally {
          setLoadingFor("");
        }
      } else {
        // No justification yet, show modal
        setJustificationType("LATE");
        setShowLateTaskJustificationModal(true);
      }
      return;
    }
    setLoadingFor("ApproveReject");
    try {
      const token = await currentUser.getIdToken();
      const res = await approveTask(systemTaskId, token);

      if (res.success) {
        toast.success("Task successfully approved!");
        reload();
      } else {
        toast.error(res.message);
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoadingFor("");
    }
  };

  const handleSaveNotes = async () => {
    if (
      !currentUser ||
      !task ||
      progressNotes.trim() === "" ||
      supervisionInstanceId === undefined
    ) {
      toast.error(
        "Cannot save notes. Missing information (Task ID or Supervision Instance)."
      );
      return;
    }

    setIsNoteLoading(true);
    try {
      const token = await currentUser.getIdToken();
      await postSupervisionComment({
        idToken: token,
        taskId: task.id,
        commentText: progressNotes,
        instanceId: supervisionInstanceId,
      });
      toast.success("Progress notes saved successfully!");
      await fetchNote(String(task.id));
    } catch (error: any) {
      console.error("Error saving progress notes:", error);
      toast.error(error.message || "Failed to save progress notes.");
    } finally {
      setIsNoteLoading(false);
    }
  };

  if (unauthorized) return <Unathorized />;

  const handleCloseAddActivity = () => setShowAddActivity(false);
  const handleShowAddActivity = () => setShowAddActivity(true);
  const handleCloseRejectTaskModal = () => setShowRejectTaskModal(false);
  const handleShowRejectTaskModal = () => setShowRejectTaskModal(true);
  const handleShowAddMeetingModal = () => setShowAddMeetingModal(true);
  const handleCloseAddMeetingModal = () => setShowAddMeetingModal(false);
  const handleShowExtendTaskRequestModal = () =>
    setShowExtendTaskRequestModal(!showExtendTaskRequestModal);
  const handleShowCloseTaskRequestModal = () =>
    setShowCloseTaskRequestModal(!showCloseTaskRequestModal);
  const handleCloseUpdateActivityModal = () => {
    setCurrentActivity(undefined);
    setShowUpdateActivityModal(false);
  };
  const handleShowUpdateActivityModal = (taskAction: TaskAction) => {
    setCurrentActivity(taskAction);
    setShowUpdateActivityModal(true);
  };

  const getHighestRole = (): string | null => {
    if (!currentRoles || currentRoles.length === 0) return null;
    let highestRole: string | null = null;
    let highestPriority = 0;
    currentRoles.forEach((role) => {
      const roleName: string = role.name;
      if (!roleName) return;
      const priority = ROLE_PRIORITY[roleName as keyof typeof ROLE_PRIORITY] || 0;
      if (priority > highestPriority) {
        highestPriority = priority;
        highestRole = roleName;
      }
    });
    return highestRole;
  };
  const highestRole = getHighestRole();
  const isSC = highestRole === ROLES.SC;
  const isProgramManagerSupervisorOrOwner =
    highestRole === ROLES.PROGRAM_MANAGER ||
    highestRole === ROLES.SUPERVISOR ||
    highestRole === ROLES.OWNER;

  const hasNullApprovalDate = workflowItems?.some(
    (obj) =>
      obj.stepOrder === 1 &&
      (!obj.taskApproval || obj.taskApproval.approvalDate === undefined)
  );
  const filteredList = workflowItems?.filter(
    (obj) => obj.taskApproval?.approvalDate === undefined
  );
  const maxStepOrderObject =
    filteredList && filteredList.length > 0
      ? filteredList.reduce((prev, current) =>
        prev.stepOrder < current.stepOrder ? prev : current
      )
      : undefined;
  const isInCurrentUserRoles = currentRoles?.some(
    (role) => role.name === maxStepOrderObject?.role.name || role.name === "OWNER" || role.name === "PROGRAM MANAGER"
  );

  return (
    <div className="task-page-container">
      <div className="top-bar">
        <div className="breadcrumbs">
          <span
            className="cursor-pointer text-[#7e57c2]"
            onClick={() => navigate('/tasks')}
          >
            Tasks
          </span>
          <span className="breadcrumb-separator">{">"}</span>
          <span className="breadcrumb-active">
            {task?.title ?? "Task Details"}
          </span>
        </div>
      </div>

      {isLoadingTask ? (
        <div className="loading-container">
          <Spinner size="sm" /> <span className="ms-2">Loading Task...</span>
        </div>
      ) : !task ? (
        <div className="no-task-found">No Task Found</div>
      ) : (
        <div className="task-content-container">
          <div className="task-body-layout">
            <div className="task-main-content">
              {/* HEADER SECTION */}
              <div className="task-title-header">
                <div className="task-title-section">
                  <div className="task-title-content">
                    <h2 className="inline-flex items-center gap-2 flex-wrap m-0">
                      {task.title}{" "}
                      <span
                        className={`task-badge capitalize text-black flex items-center justify-center rounded-[6px] whitespace-nowrap ${task.subType?.type === "MONTHLY"
                          ? "bg-[#EA9CEB]"
                          : task.subType?.type === "QUARTERLY"
                            ? "bg-[#C1F1F1]"
                            : task.subType?.type === "INTERIM"
                              ? "bg-[#9CC1EB]"
                              : "bg-[#F6DDD8]"
                          }`}
                      >
                        {task.subType?.type || "Monthly"}
                      </span>
                    </h2>

                    <ClickToCopyDiv
                      textToCopy={`${window.location.origin}/task/${task.organization.abbreviation}-${task.friendlyId}`}
                      className="task-id-copy"
                    >
                      <span className="task-id-copy-text-label">
                        Task ID:{" "}
                        <span className="task-id-copy-text">
                          {task.organization.abbreviation}-{task.friendlyId}
                        </span>
                      </span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14.225 14.75H5.65C5.51076 14.75 5.37723 14.6947 5.27877 14.5962C5.18031 14.4978 5.125 14.3642 5.125 14.225V5.65C5.125 5.51076 5.18031 5.37723 5.27877 5.27877C5.37723 5.18031 5.51076 5.125 5.65 5.125H14.225C14.3642 5.125 14.4978 5.18031 14.5962 5.27877C14.6947 5.37723 14.75 5.51076 14.75 5.65V14.225C14.75 14.3642 14.6947 14.4978 14.5962 14.5962C14.4978 14.6947 14.3642 14.75 14.225 14.75Z"
                          stroke="black"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10.375 5.125V1.275C10.375 1.13576 10.3197 1.00223 10.2212 0.903769C10.1228 0.805312 9.98924 0.75 9.85 0.75H1.275C1.13576 0.75 1.00223 0.805312 0.903769 0.903769C0.805312 1.00223 0.75 1.13576 0.75 1.275V9.85C0.75 9.98924 0.805312 10.1228 0.903769 10.2212C1.00223 10.3197 1.13576 10.375 1.275 10.375H5.125"
                          stroke="black"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </ClickToCopyDiv>
                  </div>
                </div>
                <div className="task-title-actions">
                  {(
                    task.status.name.toLowerCase() !== "pending extension" && task.status.name.toLowerCase() !== "deleted" && task.status.name.toLowerCase() !== "pending deletion" && task.status.name.toLowerCase() !== "completed" && <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-closure">
                          {closureRequest
                            ? `Deletion requested by ${closureRequest.requesterName}`
                            : "Request Deletion"}
                        </Tooltip>
                      }
                    >
                      <Button
                        variant="outline"
                        className="request-deletion-btn-header"
                        onClick={
                          !!closureRequest
                            ? undefined
                            : handleShowCloseTaskRequestModal
                        }
                        disabled={!!closureRequest}
                      >
                        Request Deletion
                      </Button>
                    </OverlayTrigger>
                  )}
                  {(isSC && isInCurrentUserRoles) && (
                    <Button
                      className="submit-btn-header"
                      onClick={() => {
                        if (
                          isInCurrentUserRoles &&
                          task.isReady &&
                          task.status.name !== "COMPLETED"
                        ) {
                          HandleApprove();
                        }
                      }}
                      disabled={lodingFor === "ApproveReject"}
                    >
                      Submit
                    </Button>
                  )}
                  {(isProgramManagerSupervisorOrOwner && isInCurrentUserRoles && task.status.name.toLowerCase() !== "completed" && task.status.name.toLowerCase() !== "deleted") && (
                    <>
                      {
                        task.isReady &&
                        task.status.name !== "COMPLETED" && (
                          <Button
                            variant="success"
                            className="approve-btn btn-txt"
                            onClick={HandleApprove}
                            disabled={lodingFor === "ApproveReject"}
                          >
                            Approve
                          </Button>
                        )}
                      {!hasNullApprovalDate && (
                        <Button
                          variant="danger"
                          className="reject-btn btn-txt"
                          onClick={handleShowRejectTaskModal}
                          disabled={lodingFor === "ApproveReject"}
                        >
                          Reject
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="task-description-section">
                <h4 className="flex items-center gap-2">Description
                  <div className="instructions-icon-wrapper" style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    {showInstructionsPopup && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="2.5rem"
                        height="2.5rem"
                        viewBox="0 0 40 40"
                        fill="none"
                        className="instructions-icon-background"
                        style={{
                          position: "absolute",
                          width: "2.5rem",
                          height: "2.5rem",
                          aspectRatio: "1/1",
                          zIndex: 0,
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          pointerEvents: "none",
                        }}
                      >
                        <circle cx="20" cy="20" r="20" fill="#FFF" />
                      </svg>
                    )}
                    <svg
                      ref={instructionsIconRef}
                      className={`cursor-pointer instructions-icon ${showInstructionsPopup ? 'instructions-icon-active' : ''}`}
                      onClick={() => setShowInstructionsPopup(!showInstructionsPopup)}
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 17 17"
                      fill="none"
                      style={{
                        width: '15px',
                        height: '15px',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <path d="M8.33333 12.5C8.56944 12.5 8.7675 12.42 8.9275 12.26C9.0875 12.1 9.16722 11.9022 9.16667 11.6667V8.33333C9.16667 8.09722 9.08667 7.89945 8.92667 7.74C8.76667 7.58056 8.56889 7.50056 8.33333 7.5C8.09778 7.49944 7.9 7.57944 7.74 7.74C7.58 7.90056 7.5 8.09833 7.5 8.33333V11.6667C7.5 11.9028 7.58 12.1008 7.74 12.2608C7.9 12.4208 8.09778 12.5006 8.33333 12.5ZM8.33333 5.83333C8.56944 5.83333 8.7675 5.75333 8.9275 5.59333C9.0875 5.43333 9.16722 5.23556 9.16667 5C9.16611 4.76444 9.08611 4.56667 8.92667 4.40667C8.76722 4.24667 8.56944 4.16667 8.33333 4.16667C8.09722 4.16667 7.89945 4.24667 7.74 4.40667C7.58056 4.56667 7.50056 4.76444 7.5 5C7.49944 5.23556 7.57945 5.43361 7.74 5.59417C7.90056 5.75472 8.09833 5.83444 8.33333 5.83333ZM8.33333 16.6667C7.18056 16.6667 6.09722 16.4478 5.08333 16.01C4.06945 15.5722 3.1875 14.9786 2.4375 14.2292C1.6875 13.4797 1.09389 12.5978 0.656668 11.5833C0.219446 10.5689 0.00055661 9.48556 1.05485e-06 8.33333C-0.000554501 7.18111 0.218334 6.09778 0.656668 5.08333C1.095 4.06889 1.68861 3.18694 2.4375 2.4375C3.18639 1.68806 4.06833 1.09444 5.08333 0.656667C6.09833 0.218889 7.18167 0 8.33333 0C9.485 0 10.5683 0.218889 11.5833 0.656667C12.5983 1.09444 13.4803 1.68806 14.2292 2.4375C14.9781 3.18694 15.5719 4.06889 16.0108 5.08333C16.4497 6.09778 16.6683 7.18111 16.6667 8.33333C16.665 9.48556 16.4461 10.5689 16.01 11.5833C15.5739 12.5978 14.9803 13.4797 14.2292 14.2292C13.4781 14.9786 12.5961 15.5725 11.5833 16.0108C10.5706 16.4492 9.48722 16.6678 8.33333 16.6667ZM8.33333 15C10.1944 15 11.7708 14.3542 13.0625 13.0625C14.3542 11.7708 15 10.1944 15 8.33333C15 6.47222 14.3542 4.89583 13.0625 3.60417C11.7708 2.3125 10.1944 1.66667 8.33333 1.66667C6.47222 1.66667 4.89583 2.3125 3.60417 3.60417C2.3125 4.89583 1.66667 6.47222 1.66667 8.33333C1.66667 10.1944 2.3125 11.7708 3.60417 13.0625C4.89583 14.3542 6.47222 15 8.33333 15Z" fill="#3E3E3E" />
                    </svg>
                  </div>
                </h4>
                <p>{task.description}</p>
              </div>

              <div className="task-attachments-section-main">
                <TaskAttachmentSection
                  task={task}
                  taskId={String(task.id)}
                  reload={reload}
                  participantId={task?.participant.id}
                  onAttachmentCountChange={setAttachmentCount}
                />
              </div>

              <div className="task-activity-section">
                <div className="activity-tabs">
                  <span
                    className={`tab ${activeMainTab === "activity" ? "active" : ""
                      }`}
                    onClick={() => setActiveMainTab("activity")}
                  >
                    Activity <span>{activities?.length || 0}</span>
                  </span>
                  <span
                    className={`tab comments-tab ${activeMainTab === "comments" ? "active" : ""
                      }`}
                    onClick={() => setActiveMainTab("comments")}
                  >
                    Comments <span>{task.commentsCount ?? 0}</span>
                  </span>
                  <span
                    className={`tab ${activeMainTab === "meetings" ? "active" : ""
                      }`}
                    onClick={() => setActiveMainTab("meetings")}
                  >
                    Meeting <span>{task.meetings?.length ?? 0}</span>
                  </span>
                </div>
                <div className="tab-content">
                  {activeMainTab === "activity" && (
                    <TaskActivitySection
                      activities={activities}
                      currentUser={currentUser}
                      currentRoles={currentRoles}
                      formatActivityTime={formatActivityTime}
                      onEditActivity={handleShowUpdateActivityModal}
                      onAddActivity={handleShowAddActivity}
                    />
                  )}
                  {activeMainTab === "comments" && (
                    <div className="comments-container">
                      <TaskChatComponent
                        taskId={task.id}
                        assignees={task.assignees}
                        onCommentAdded={(count: number) => {
                          if (task) {
                            setTask({
                              ...task,
                              commentsCount: count
                            });
                          }
                        }}
                      />
                    </div>
                  )}
                  {activeMainTab === "meetings" && (
                    <>
                      <TaskMeetingSection
                        task={task}
                        taskId={String(task.id)}
                        reload={reload}
                        participantId={task?.participant.id}
                        meetings={task.meetings}
                      />
                      <Button
                        className="add-activity-btn"
                        onClick={() => setIsModalOpen(true)}
                      >
                        Add Meeting
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* EXTRACTED SIDEBAR COMPONENT */}
            <TaskSidebar
              task={task}
              activities={activities}
              currentUser={currentUser}
              progressNotes={progressNotes}
              setProgressNotes={setProgressNotes}
              handleSaveNotes={handleSaveNotes}
              isNoteLoading={isNoteLoading}
              progressNoteData={progressNoteData}
              closureRequest={closureRequest}
              handleShowExtendTaskRequestModal={handleShowExtendTaskRequestModal}
            />

          </div>
        </div>
      )}
      {isModalOpen && (
        <ModalMeet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <AddEvent
            meetingTask={task}
            onClose={() => setIsModalOpen(false)}
            onEventCreated={handleMeetingCreated}
          />
        </ModalMeet>
      )}
      {task && (
        <Modal
          show={showAddActivity}
          onHide={handleCloseAddActivity}
          centered
        >
          <CreateTaskActivityComponent
            taskIdFromProp={String(task.id)}
            onActivityCreated={handleActivityCreated}
          />
        </Modal>
      )}
      {task && (
        <LateTaskJustificationModal
          show={showLateTaskJustificationModal}
          onHide={() => setShowLateTaskJustificationModal(false)}
          taskId={Number(task.id)}
          justificationType={justificationType}
          onSuccess={async () => {
            setLoadingFor("ApproveReject");
            try {
              if (!currentUser) {
                toast.error("User not authenticated.");
                return;
              }
              const token = await currentUser.getIdToken();
              const res = await approveTask(String(task.id), token);
              if (res.success) {
                toast.success("Task successfully approved!");
                reload();
              } else {
                toast.error(res.message);
              }
            } catch (err: any) {
              toast.error(err.message);
            } finally {
              setLoadingFor("");
            }
          }}
        />
      )}
      {task && (
        <RequestTaskClosureModal
          show={showCloseTaskRequestModal}
          onHide={() => setShowCloseTaskRequestModal(false)}
          taskId={Number(task.id)}
          onSuccess={reload}
        />
      )}
      {task && (
        <RequestTaskExtensionModal
          show={showExtendTaskRequestModal}
          onHide={() => setShowExtendTaskRequestModal(false)}
          taskId={Number(task.id)}
          onSuccess={reload}
        />
      )}
      {showRejectTaskModal && authToken && task && (
        <RejectTaskModal
          handleCloseRejectTaskModal={handleCloseRejectTaskModal}
          handleShowRejectTaskModal={handleShowRejectTaskModal}
          showRejectTaskModal={showRejectTaskModal}
          workflowItems={workflowItems}
          authToken={authToken}
          taskId={String(task.id)}
          reload={reload}
          setLoadingFor={setLoadingFor}
        />
      )}
      {showUpdateActivityModal && currentActivity && (
        <UpdateTaskActivityModal
          handleCloseUpdateActivityModal={handleCloseUpdateActivityModal}
          showUpdateActivityModal={showUpdateActivityModal}
          taskAction={currentActivity}
          authToken={authToken}
          refreshActivity={reload}
        />
      )}
      {showAddMeetingModal && task && (
        <Modal show={showAddMeetingModal} onHide={handleCloseAddMeetingModal}>
          <Modal.Body>
            <AddTaskMeetingModal
              onClose={handleCloseAddMeetingModal}
              task={task}
              onEventCreated={reload}
            />
          </Modal.Body>
        </Modal>
      )}

      {/* Instructions Popup */}
      {showInstructionsPopup && task && instructionsIconRef.current && (
        <InstructionsPopup
          task={task}
          anchorElement={instructionsIconRef.current}
          onClose={() => setShowInstructionsPopup(false)}
        />
      )}
    </div>
  );
};

export default TaskPage;